/**
 * Mojo Up's operators' break-glass tools (docs/operator-access.md), on the Operate page: the grants opened in the
 * last 30 days, opening one (a reason and at most four hours), reading an organisation in brief under it, and closing
 * it early. The customer sees each grant and every read under it on their own Mojo Up staff access page.
 */

import { FileText, LockOpen } from '@gravity-ui/icons';
import { Button, Input, Label, TextArea, TextField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { OPERATOR_ACCESS_STATE_WORDS, auditOriginWords, type OperatorAccessGrant, type OperatorAccessGrantList, type OperatorAccessState } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';

import { operatorAccessRoutes, problemText } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { ArtEmpty, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from '../ui.tsx';
import type { ArtTone } from '../ui/Art.tsx';
import { FormModal } from './FormModal.tsx';

const MARK: Record<OperatorAccessState, 'online' | 'idle' | 'offline'> = { open: 'online', expired: 'idle', closed: 'offline' };

export const accessWhen = (iso: string) => dateTime(iso, { date: 'medium', year: false });

const LENGTHS = [
  { id: '30', label: '30 min' },
  { id: '60', label: '1 hour' },
  { id: '120', label: '2 hours' },
  { id: '240', label: '4 hours' },
];

/** The grants panel: every operator's, open and from the last 30 days. */
export function StaffAccessGrants({ tone, grants, onOpen, onChanged }: { tone: ArtTone; grants: OperatorAccessGrantList | undefined; onOpen: () => void; onChanged: () => void }) {
  const [looking, setLooking] = useState<OperatorAccessGrant>();
  const [error, setError] = useState<string>();
  const rows = grants?.grants ?? [];
  const open = rows.filter((g) => g.state === 'open');
  const close = async (g: OperatorAccessGrant) => {
    setError(undefined);
    try {
      await operatorAccessRoutes.close(g.id);
      onChanged();
    } catch (e) {
      setError(problemText(e));
    }
  };
  return (
    <>
      <ListGroup
        title="Staff access"
        count={open.length || undefined}
        description="Open and from the last 30 days, every operator's; each organisation sees its own."
        actions={
          <Button size="sm" variant="secondary" onPress={onOpen}>
            Open access
          </Button>
        }
        empty={
          grants && rows.length === 0 && !error ? (
            <ArtEmpty tone={tone} icon={<LockOpen />} title="No access opened" compact>
              Before reading a customer's organisation, open access to it: say why and for how long. The organisation sees the grant and every read under it.
            </ArtEmpty>
          ) : undefined
        }
      >
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
        {rows.map((g) => (
          <ListRow
            key={g.id}
            icon={<LockOpen />}
            title={g.organisationName ?? g.organisationId}
            description={
              <>
                <span className="block break-words text-foreground">{g.reason}</span>
                <span className="block">
                  {g.operator.name} · opened {accessWhen(g.openedAt)} · {g.state === 'open' ? `until ${accessWhen(g.expiresAt)}` : g.closedAt ? `closed ${accessWhen(g.closedAt)}${g.closedBy ? ` by ${g.closedBy.name}` : ''}` : `ended ${accessWhen(g.expiresAt)}`}
                </span>
              </>
            }
            {...(g.state === 'open'
              ? {
                  compactControl: true,
                  control: (
                    <>
                      <Button size="sm" variant="secondary" onPress={() => setLooking(g)}>
                        Look
                      </Button>
                      <Button size="sm" variant="ghost" onPress={() => void close(g)}>
                        Close
                      </Button>
                    </>
                  ),
                }
              : { status: <StatusMark state={MARK[g.state]} label={OPERATOR_ACCESS_STATE_WORDS[g.state]} /> })}
          />
        ))}
      </ListGroup>
      {looking && <LookAt grant={looking} onClose={() => setLooking(undefined)} />}
    </>
  );
}

/** Open a grant to read one organisation: which, why, and for how long. */
export function OpenAccessModal({ organisationId, onClose, onDone }: { organisationId?: string; onClose: () => void; onDone: () => void }) {
  const [organisation, setOrganisation] = useState(organisationId ?? '');
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState('60');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await operatorAccessRoutes.open({ organisationId: organisation.trim(), reason: reason.trim(), minutes: Number(minutes) });
      onDone();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormModal
      isOpen
      onClose={onClose}
      title="Open access"
      blurb="For reading only. The organisation sees who opened it, why and until when, and every read under it."
      submitLabel="Open access"
      canSubmit={!!organisation.trim() && reason.trim().length >= 10}
      busy={busy}
      error={error}
      onSubmit={() => void submit()}
    >
      <TextField variant="secondary" value={organisation} onChange={setOrganisation} isRequired autoFocus={!organisationId}>
        <Label>Organisation</Label>
        <Input placeholder="Its id or short name" maxLength={200} />
      </TextField>
      <TextField className="w-full" variant="secondary" value={reason} onChange={setReason} isRequired autoFocus={!!organisationId}>
        <Label>Why</Label>
        <TextArea className="w-full resize-y" rows={3} maxLength={1000} placeholder="Support request 4411: their nodes stopped reporting on Monday." />
      </TextField>
      <div className="grid gap-1.5">
        <Label>For how long</Label>
        <Segment aria-label="For how long" size="sm" selectedKey={minutes} onSelectionChange={(k) => setMinutes(String(k))} className="w-fit">
          {LENGTHS.map((l) => (
            <Segment.Item key={l.id} id={l.id}>
              {l.label}
            </Segment.Item>
          ))}
        </Segment>
      </div>
    </FormModal>
  );
}

/** What an operator reads under a grant: the organisation in brief and its latest audit rows. Each open is itself a read on the record. */
function LookAt({ grant, onClose }: { grant: OperatorAccessGrant; onClose: () => void }) {
  const summary = useLoad(() => operatorAccessRoutes.organisation(grant.organisationId), [grant.organisationId]);
  const trail = useLoad(() => operatorAccessRoutes.organisationAudit(grant.organisationId), [grant.organisationId]);
  const s = summary.data;
  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={s?.name ?? grant.organisationName ?? 'Organisation'}
      blurb={`Read under your grant until ${accessWhen(grant.expiresAt)}. Each look is on the organisation's record.`}
      submitLabel="Done"
      onSubmit={onClose}
      footer={
        <Button variant="primary" onPress={onClose}>
          Done
        </Button>
      }
    >
      <ErrorNote error={summary.error ?? trail.error} />
      {s && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">Short name</dt>
          <dd className="font-mono">{s.slug}</dd>
          <dt className="text-muted">Kind</dt>
          <dd>{s.kind === 'team' ? 'Team' : s.kind === 'personal' ? 'Personal' : 'Enterprise'}</dd>
          <dt className="text-muted">Created</dt>
          <dd>{accessWhen(s.createdAt)}</dd>
          <dt className="text-muted">Machines</dt>
          <dd className="tabular-nums">{s.machines}</dd>
          {s.members !== undefined && (
            <>
              <dt className="text-muted">Members</dt>
              <dd className="tabular-nums">{s.members}</dd>
            </>
          )}
        </dl>
      )}
      <div className="grid gap-1">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <FileText className="size-4 text-muted" />
          Latest on its audit trail, your own reads left out
        </div>
        <ul className="max-h-64 divide-y divide-separator overflow-y-auto text-sm">
          {(trail.data?.entries ?? [])
            .filter((e) => e.action !== 'operator.access.read')
            .map((e, i) => (
              <li key={`${e.ts}-${i}`} className="py-1.5">
                <div className="break-words">{e.action}</div>
                <div className="text-xs text-muted">
                  {accessWhen(e.ts)}
                  {e.target ? ` · ${e.target}` : ''}
                </div>
                {auditOriginWords(e.origin) ? <div className="truncate text-xs text-muted">{auditOriginWords(e.origin)}</div> : null}
              </li>
            ))}
        </ul>
      </div>
    </FormModal>
  );
}
